import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, RecaptchaVerifier, getMultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator, multiFactor } from 'firebase/auth';

// Configure Axios globally
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [verificationId, setVerificationId] = useState(null);
    const [authError, setAuthError] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    // Get the Firebase ID token
                    const token = await firebaseUser.getIdToken();
                    // Set it in Axios headers for all future requests
                    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    
                    // Fetch user details from our Postgres database
                    const response = await axios.get('/users/me');
                    setUser({ ...response.data, firebaseUser });
                    setAuthError(null);
                } catch (error) {
                    console.error("Error fetching user data from backend", error);
                    setUser(null);
                    // Clear the invalid token
                    delete axios.defaults.headers.common['Authorization'];
                    
                    // If the user doesn't exist in our DB, sign them out of Firebase too
                    if (error.response && error.response.status === 404) {
                        setAuthError("Please create an account first before logging in.");
                        await signOut(auth);
                    } else {
                        setAuthError("An error occurred while fetching your profile.");
                    }
                }
            } else {
                setUser(null);
                delete axios.defaults.headers.common['Authorization'];
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // onAuthStateChanged will handle the rest
            return { requiresMFA: false };
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, error);
                setMfaResolver(resolver);
                return { requiresMFA: true, resolver };
            }
            throw error;
        }
    };

    const loginWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            // onAuthStateChanged will handle the rest
            return { requiresMFA: false };
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, error);
                setMfaResolver(resolver);
                return { requiresMFA: true, resolver };
            }
            throw error;
        }
    };

    const sendMfaSms = async (phoneNumber, containerId) => {
        if (!mfaResolver) throw new Error("No MFA resolver found.");
        
        const recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
            size: 'invisible'
        });

        const phoneInfoOptions = {
            multiFactorHint: mfaResolver.hints[0],
            session: mfaResolver.session
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
        setVerificationId(verificationId);
        return verificationId;
    };

    const verifyMfaCode = async (verificationCode) => {
        if (!mfaResolver || !verificationId) throw new Error("Missing MFA state.");
        
        const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
        
        // This completes the sign-in
        await mfaResolver.resolveSignIn(multiFactorAssertion);
        setMfaResolver(null);
        setVerificationId(null);
    };

    const logout = async () => {
        try {
            await signOut(auth);
            // Our backend no longer handles session state, just the token
        } catch (error) {
            console.error("Error during logout", error);
        }
    };

    const toggleTheme = async (darkMode) => {
        try {
            await axios.patch('/users/me/theme', { dark_mode: darkMode });
            setUser(prev => ({ ...prev, dark_mode: darkMode }));
        } catch (error) {
            console.error("Error updating theme", error);
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            login, 
            loginWithGoogle,
            logout, 
            loading, 
            authError,
            setAuthError,
            toggleTheme,
            sendMfaSms,
            verifyMfaCode
        }}>
            {children}
        </AuthContext.Provider>
    );
};